/**
 * SceneEnvironmentComponent - A comprehensive scene environment system for 3D environments.
 * Supports texture-based environments (HDR/EXR panoramas) and procedural gradient backgrounds.
 * Provides automatic environment mapping for realistic material reflections.
 */

import * as THREE from 'three';

import { NetRuntime } from '../../multiplayer/NetRuntime.js';
import { resolvePath } from '../../storage/projectContext.js';
import { AssetPath } from '../../storage/storageProvider.js';
import { ClassRegistry, EngineClass } from '../../systems/ClassRegistry.js';
import { getLatest } from '../../utils/Async.js';
import { resourceManager } from '../../utils/ResourceManager.js';
import { property } from '../../utils/serialization/decorator.js';
import { SceneComponent } from '../SceneComponent.js';

import type { ISceneEnvironmentSource, SceneEnvironmentData } from '../../interfaces.js';
import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';
import type { HdriPath } from '../../utils/serialization/decorator.js';
import type { SceneComponentOptions } from '../SceneComponent.js';

export enum EnvironmentSourceType {
  Textured = 'Textured',
  Procedural = 'Procedural',
}

export interface TextureEnvironmentOptions extends SceneComponentOptions {
  sourceType?: EnvironmentSourceType.Textured;
  textureUrl: string;
  backgroundIntensity?: number;
  backgroundBlurriness?: number;
  backgroundRotation?: THREE.Euler;
  envMapIntensity?: number;
  envMapRotation?: THREE.Euler;
}

export interface ProceduralEnvironmentOptions extends SceneComponentOptions {
  sourceType?: EnvironmentSourceType.Procedural;
  width: number;
  height: number;
  bottomColor: THREE.ColorRepresentation;
  middleColor: THREE.ColorRepresentation;
  topColor?: THREE.ColorRepresentation;
  backgroundIntensity?: number;
  backgroundBlurriness?: number;
  backgroundRotation?: THREE.Euler;
  envMapIntensity?: number;
  envMapRotation?: THREE.Euler;
}

export type SceneEnvironmentOptions = TextureEnvironmentOptions | ProceduralEnvironmentOptions;

@EngineClass('SceneEnvironmentComponent')
export class SceneEnvironmentComponent extends SceneComponent implements ISceneEnvironmentSource {
  @property({ type: 'enum', options: Object.values(EnvironmentSourceType), migrateFrom: 'skyboxType' })
  public sourceType: EnvironmentSourceType = EnvironmentSourceType.Textured;

  @property()
  public textureUrl: HdriPath = '';

  @property({ type: 'number', min: 0 })
  public backgroundIntensity: number = 1.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.1 })
  public backgroundBlurriness: number = 0.0;

  @property()
  public backgroundRotation: THREE.Euler = new THREE.Euler(0, 0, 0);

  @property({ type: 'number', min: 0 })
  public envMapIntensity: number = 1.0;

  @property()
  public envMapRotation: THREE.Euler = new THREE.Euler(0, 0, 0);

  @property()
  public width: number = 2048;

  @property()
  public height: number = 1024;

  @property()
  public bottomColor: THREE.ColorRepresentation = '#E6F3FF';

  @property()
  public middleColor: THREE.ColorRepresentation = '#4A90E2';

  @property()
  public topColor: THREE.ColorRepresentation = '#0F1B3C';

  private texture: THREE.Texture | null = null;


  public override initialize(options?: SceneEnvironmentOptions): void {
    super.initialize(options);

    this.backgroundIntensity = options?.backgroundIntensity ?? this.backgroundIntensity;
    this.backgroundBlurriness = options?.backgroundBlurriness ?? this.backgroundBlurriness;
    this.backgroundRotation = options?.backgroundRotation ?? this.backgroundRotation;
    this.envMapIntensity = options?.envMapIntensity ?? this.envMapIntensity;
    this.envMapRotation = options?.envMapRotation ?? this.envMapRotation;

    const textureOpts = options as TextureEnvironmentOptions | undefined;
    const proceduralOpts = options as ProceduralEnvironmentOptions | undefined;

    // Determine source type: explicit sourceType wins, then infer from options
    if (options?.sourceType !== undefined) {
      this.sourceType = options.sourceType;
    } else if (textureOpts?.textureUrl !== undefined) {
      this.sourceType = EnvironmentSourceType.Textured;
    } else if (proceduralOpts?.width !== undefined || proceduralOpts?.height !== undefined || proceduralOpts?.bottomColor !== undefined || proceduralOpts?.middleColor !== undefined) {
      this.sourceType = EnvironmentSourceType.Procedural;
    }

    if (textureOpts?.textureUrl !== undefined) {
      this.textureUrl = textureOpts.textureUrl;
    }

    if (proceduralOpts?.width !== undefined) this.width = proceduralOpts.width;
    if (proceduralOpts?.height !== undefined) this.height = proceduralOpts.height;
    if (proceduralOpts?.bottomColor !== undefined) this.bottomColor = proceduralOpts.bottomColor;
    if (proceduralOpts?.middleColor !== undefined) this.middleColor = proceduralOpts.middleColor;
    if (proceduralOpts?.topColor !== undefined) this.topColor = proceduralOpts.topColor;
  }

  public createTextureEnvironment(options: TextureEnvironmentOptions): void {
    this.initialize(options);
    this.loadTexture();
  }

  public createProceduralEnvironment(options: ProceduralEnvironmentOptions): void {
    this.initialize(options);
    this.loadTexture();
  }

  public override beginPlay(): void {
    super.beginPlay();

    // Skip on server - no rendering/textures needed
    if (NetRuntime.isDedicatedServer()) {
      return;
    }

    this.getWorld()?.getRenderScene().addSceneEnvironment(this);
    this.loadTexture();
  }

  public override endPlay(): void {
    this.getWorld()?.getRenderScene().removeSceneEnvironment(this);
    super.endPlay();
    if (this.texture) {
      this.texture.dispose();
      this.texture = null;
    }
  }

  public getTexture(): THREE.Texture | null {
    return this.texture;
  }

  //====================================================================================================

  private static async loadPanoramaTexture(path: AssetPath): Promise<THREE.Texture | null> {
    path = await resolvePath(path);
    const loadedTexture = await resourceManager.loadTexture(path);
    if (!loadedTexture) {
      console.error(`Failed to load texture from ${path}`);
      return null;
    }

    loadedTexture.mapping = THREE.EquirectangularReflectionMapping;
    loadedTexture.format = THREE.RGBAFormat;
    loadedTexture.generateMipmaps = false;
    loadedTexture.minFilter = THREE.LinearFilter;
    loadedTexture.magFilter = THREE.LinearFilter;
    loadedTexture.colorSpace = THREE.LinearSRGBColorSpace;
    return loadedTexture;
  }

  private static createProceduralPanoramaTexture(params: ProceduralEnvironmentOptions): THREE.Texture {
    const { width, height, bottomColor, middleColor, topColor = middleColor } = params;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;

    // Helper function to convert color to CSS string
    const colorToCSS = (color: THREE.ColorRepresentation): string => {
      if (color instanceof THREE.Color) {
        return `#${color.getHexString()}`;
      } else if (typeof color === 'string') {
        return color;
      } else if (typeof color === 'number') {
        return `#${color.toString(16).padStart(6, '0')}`;
      } else {
        // Handle other color representations (arrays, etc.)
        const threeColor = new THREE.Color(color);
        return `#${threeColor.getHexString()}`;
      }
    };

    // Create simple gradient
    const gradient = ctx.createLinearGradient(0, height, 0, 0);
    gradient.addColorStop(0, colorToCSS(bottomColor));
    gradient.addColorStop(0.5, colorToCSS(middleColor));
    gradient.addColorStop(1, colorToCSS(topColor));

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    const texture = new THREE.CanvasTexture(canvas);
    return this.asPanoramaTexture(texture);
  }

  private async loadTexture(): Promise<void> {
    if (this.texture) {
      this.texture.dispose();
      this.texture = null;
    }

    if (this.sourceType === EnvironmentSourceType.Textured) {
      // Use getLatest to prevent race conditions when textureUrl changes rapidly
      const loadedTexture = await getLatest(
        this,
        'texture',
        () => SceneEnvironmentComponent.loadPanoramaTexture(AssetPath.fromString(this.textureUrl))
      );
      if (loadedTexture === undefined) {
        return; // A newer load superseded this one
      }
      this.texture = loadedTexture;
    } else {
      this.texture = SceneEnvironmentComponent.createProceduralPanoramaTexture({
        width: this.width,
        height: this.height,
        bottomColor: this.bottomColor,
        middleColor: this.middleColor,
        topColor: this.topColor,
      });
    }
  }

  private static asPanoramaTexture(texture: THREE.Texture): THREE.Texture {
    texture.mapping = THREE.EquirectangularReflectionMapping;
    texture.format = THREE.RGBAFormat;
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    return texture;
  }

  public getSceneEnvironmentData(): SceneEnvironmentData {
    return {
      sceneComponent: this,
      background: this.texture,
      backgroundIntensity: this.backgroundIntensity,
      backgroundBlurriness: this.backgroundBlurriness,
      backgroundRotation: this.backgroundRotation,
      environment: this.texture,
      environmentIntensity: this.envMapIntensity,
      environmentRotation: this.envMapRotation,
    };
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override onEditorAddToWorld(): void {
    super.onEditorAddToWorld();
    this.loadTexture();
    this.getWorld()?.getRenderScene().addSceneEnvironment(this);
  }

  public override onEditorRemoveFromWorld(): void {
    if (this.texture) {
      this.texture.dispose();
      this.texture = null;
    }
    this.getWorld()?.getRenderScene().removeSceneEnvironment(this);
    super.onEditorRemoveFromWorld();
  }

  private editorLoadTextureTimeout: ReturnType<typeof setTimeout> | null = null;

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);
    if (path === 'visible' && (this.isHidden() || this.isHiddenInGame())) {
      return;
    }

    // Debounce loadTexture
    if (this.editorLoadTextureTimeout) {
      clearTimeout(this.editorLoadTextureTimeout);
    }
    this.editorLoadTextureTimeout = setTimeout(() => {
      this.loadTexture();
      this.editorLoadTextureTimeout = null;
    }, 300); // 300ms delay
  }
  public override getEditorClassIcon(): string | null {
    return 'Icon_Skybox';
  }
}

ClassRegistry.addClassesToRedirect({
  'ENGINE.SkyboxComponent': 'ENGINE.SceneEnvironmentComponent',
});

/**
 * @deprecated Use {@link SceneEnvironmentComponent} instead.
 */
export const SkyboxComponent = SceneEnvironmentComponent;

/**
 * @deprecated Use {@link SceneEnvironmentComponent} instead.
 */
export type SkyboxComponent = SceneEnvironmentComponent;

/**
 * @deprecated Use {@link EnvironmentSourceType} instead.
 */
export const SkyboxType = EnvironmentSourceType;

/**
 * @deprecated Use {@link TextureEnvironmentOptions} instead.
 */
export type TextureSkyboxOptions = TextureEnvironmentOptions;

/**
 * @deprecated Use {@link ProceduralEnvironmentOptions} instead.
 */
export type ProceduralSkyboxOptions = ProceduralEnvironmentOptions;

/**
 * @deprecated Use {@link SceneEnvironmentOptions} instead.
 */
export type SkyboxOptions = SceneEnvironmentOptions;
